import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  loginStart,
  loginSuccess,
  loginFailure,
  resetAuthState
} from '../store/slices/authSlice';
import { Background } from './layout/Background';
import { BackgroundLeftText } from './layout/BackgroundLeftText';
import { authService } from '../services/AuthService';
import eyeOnIcon from '../assets/eye-on.svg';
import eyeOffIcon from '../assets/eye-off.svg';
import { PrimaryButton, TextButton } from './ui/Button/Buttons';
import { PATHS } from '../constants/Paths.constants';
import { EMAIL_REGEX } from '../constants/Validation.constants';
import { Input } from './ui/Input/Input';
import { fetchDashboard } from '../services/DashboardService';
import { handleTutorialGuideAfterLogin } from '../utils/tutorialGuide.utils';
import { featureFlags } from '../config/featureFlags';
import { selectIsSuperAdmin } from '../store/selectors/authSelectors';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useAppSelector(
    (state) => state.auth
  );
  const isSuperAdminRole = useAppSelector(selectIsSuperAdmin);

  const isFormValid =
    email.trim() !== '' && password.trim() !== '' && EMAIL_REGEX.test(email);

  const disableLoginButton = loading || (hasInteracted && !isFormValid);

  const footerText = featureFlags.signUpEnabled
    ? "Don't have an account?"
    : 'Looking to implement AI governance?';

  const footerCtaLabel = featureFlags.signUpEnabled ? (
    'Sign Up'
  ) : (
    <span className="underline">Contact Sales</span>
  );
  const footerCtaPath = featureFlags.signUpEnabled
    ? PATHS.REGISTRATION
    : 'https://eucompliai.com/request-demo/';

  useEffect(() => {
    dispatch(resetAuthState());
  }, [dispatch]);

  useEffect(() => {
    // Check if coming from successful payment
    if (searchParams.get('payment') === 'success') {
      setShowPaymentSuccess(true);
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setShowPaymentSuccess(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      dispatch(loginFailure('Please fill in all fields'));
      return;
    }

    if (!EMAIL_REGEX.test(email)) {
      dispatch(loginFailure('Please enter a valid email address'));
      return;
    }

    dispatch(loginStart());

    try {
      const response = await authService.login({ email, password });
      if (response.role === 'SUPER_ADMIN') {
        dispatch(
          loginSuccess({
            id: response.id,
            email: response.email,
            name: response.name,
            role: response.role
          })
        );

        navigate(PATHS.USER_MANAGEMENT);
        return;
      }

      if (response.checkoutUrl) {
        window.location.replace(response.checkoutUrl);
        return;
      }

      if (response.success) {
        const dashboardData = await fetchDashboard();
        const stripeUrl = dashboardData.checkoutUrl;

        if (stripeUrl) {
          window.location.replace(stripeUrl);
        } else {
          dispatch(
            loginSuccess({
              id: response.id,
              email: response.email,
              name: response.name,
              role: response.role
            })
          );

          await handleTutorialGuideAfterLogin(
            response,
            dashboardData.hasAiSystems,
            dispatch
          );

          navigate(PATHS.DASHBOARD);
        }
      } else {
        dispatch(
          loginFailure(response.message || 'Login failed. Please try again.')
        );
      }
    } catch (error: any) {
      dispatch(
        loginFailure(error.message || 'Login failed. Please try again.')
      );
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (isSuperAdminRole) {
        navigate(PATHS.USER_MANAGEMENT);
        return;
      }

      navigate(PATHS.DASHBOARD);
    }
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) {
    return null;
  }
  return (
    <div className="min-w-screen min-h-screen relative flex justify-between items-center flex-shrink-0">
      <Background />
      <BackgroundLeftText />
      <div className=" flex flex-col w-1/2 items-center justify-center gap-10 flex-shrink-0">
        <div className="w-full max-w-md" style={{ width: '458px' }}>
          {/* Payment Success Banner */}
          {showPaymentSuccess && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="text-green-500 text-2xl mr-3">✓</div>
                <div>
                  <h3 className="text-green-800 font-semibold">
                    Payment Successful!
                  </h3>
                  <p className="text-green-700 text-sm">
                    Your subscription has been activated. Please log in to
                    continue.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 py-[32px] px-[40px]">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-left text-purple-900 mb-2">
                Sign in to your account
              </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Email Address"
                id="email"
                type="email"
                required={true}
                value={email}
                maxLength={254}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setHasInteracted(true);
                }}
                error={error}
                displayError={false}
                labelClassName="label"
                inputClassName="input"
              />

              <Input
                label="Password"
                id="password"
                type={showPassword ? 'text' : 'password'}
                required={true}
                value={password}
                maxLength={72}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setHasInteracted(true);
                }}
                error={error}
                labelClassName="label"
                inputClassName="input"
              >
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={loading}
                >
                  <img
                    src={showPassword ? eyeOnIcon : eyeOffIcon}
                    alt={showPassword ? 'Hide password' : 'Show password'}
                    className="h-5 w-5 text-gray-400 hover:text-gray-600"
                  />
                </button>
              </Input>

              <PrimaryButton
                type="submit"
                size="l"
                className="w-full"
                disabled={disableLoginButton}
              >
                {loading ? 'Signing In...' : 'Log In'}
              </PrimaryButton>
            </form>

            <div className="text-center mt-6">
              <TextButton onClick={() => navigate(PATHS.FORGOT_PASSWORD)}>
                Forgot Password?
              </TextButton>
            </div>

            <div className="text-center mt-6">
              <span className="text-sm text-gray-950 font-normal">
                {footerText}
              </span>
              <Link
                to={footerCtaPath}
                className="text-sm text-purple-800 hover:text-blue-500 bg-transparent border-none cursor-pointer p-0 font-medium"
              >
                &nbsp;
                {footerCtaLabel}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
